import * as React from 'react';
import type { CalendarSurfaceStore } from '../../../index';
import { isPastDate } from 'owa-observable-datetime';
import { observer } from 'mobx-react-lite';
import { OwaDate, startOfDay, getDay } from 'owa-datetime';
import { generateNonWorkingTimes } from './calculateMultipleNonWorkingHourBlocks';
import { generateNonWorkingHoursBlock } from './calculateSingleNonWorkingHourBlocks';

import styles from './WorkingHoursContent.scss';
import { getIsDarkTheme } from 'owa-fabric-theme';
import classNames from 'classnames';

/**
 * Shows multi working hours shadings on grid column.
 *
 * Working hours have their own time zone, independent of the main time zone and
 * if these time zones are apart enough then working hours can bleed from one day
 * to the next. When this happens, we need to account not only if the current day
 * is a working day, but also if the previous or next day are working days.
 * (TODO, do we need to account for more than 1 day each way?)
 *
 * The server sends up workHours*TimeInMinutes values adjusted to the UTC time zone.
 * So these values can be in one of three states:
 * - in the previous day, if workHours*TimeInMinutes < 0;
 * - in the current day, if 0 <= workHours*TimeInMinutes < 1440;
 * - in the next day, if workHours*TimeInMinutes >= 1440.
 *
 * We have 9 theoretical cases (3x3). However, the start value is always less than end,
 * so we have 5 possible cases to deal with.
 *
 * The following table describes what we need to render on a "this" day:
 * (tz is the user's time zone, whtz is the working hours time zone)
 *
 * |   start   |    end    | tz difference | visible working hours (all subject to the day in question being a work day) |
 * | :-------: | :-------: | :-----------: | --------------------------------------------------------------------------- |
 * |    s<0    |    e<0    |  tz <<< whtz  | [next_day.start, next_day.end] (this day is rendered by previous day)       |
 * |    s<0    | 0<=e<1440 |  tz << whtz   | [0, this_day.end] and [next_day.start, 1440]                                |
 * |    s<0    |  e>=1440  |      n/a      | difference between end and start is always < 1440 (1 day)                   |
 * | 0<=s<1440 |    e<0    |      n/a      | start is always < than end                                                  |
 * | 0<=s<1440 | 0<=e<1440 |  tz ~= whtz   | [this_day.start, this_day.end]                                              |
 * | 0<=s<1440 |  e>=1440  |  tz >> whtz   | [ 0, prev_day.end] and [this_day.start, 1440]                               |
 * |  s>=1440  |    e<0    |      n/a      | start is always < than end                                                  |
 * |  s>=1440  | 0<=e<1440 |      n/a      | start is always < than end                                                  |
 * |  s>=1440  |  e>=1440  |  tz >>> whtz  | [prev_day.start, prev_day.end]                                              |
 *
 * Once the working hours are determined, we need to flip it around and
 * produce the non-working hours shadows.
 */
export default observer(function MultiWorkingHoursContent({
    surfaceStore,
    modelDate,
    workDaysWiseHoursStartTimeInMinutes,
    workDaysWiseHoursEndTimeInMinutes,
}: {
    surfaceStore: CalendarSurfaceStore;
    modelDate: OwaDate;
    workDaysWiseHoursStartTimeInMinutes?: number[][];
    workDaysWiseHoursEndTimeInMinutes?: number[][];
}) {
    const date = startOfDay(modelDate);

    // Previous days are completely shadowed
    const isPast = isPastDate(modelDate);

    // For other days, calculate the positions of start and end of working hours
    const { userOptions, dayStartMinute, dayLength } = surfaceStore;

    // Generate the clipped blocks of shadows we want to draw
    let nonWorkingTimes: { start: number; end: number }[] = [];

    const dayOfWeek = getDay(modelDate);

    // check if multiple working hour blocks provided
    if (workDaysWiseHoursStartTimeInMinutes && workDaysWiseHoursEndTimeInMinutes) {
        const { workDays } = userOptions;
        let finalWorkingTimes = generateNonWorkingTimes(
            workDaysWiseHoursStartTimeInMinutes,
            workDaysWiseHoursEndTimeInMinutes,
            date,
            isPast,
            workDays,
            dayStartMinute,
            dayLength,
            dayOfWeek
        );

        nonWorkingTimes = [...finalWorkingTimes];
    }

    // If above condition is true but internal functions fail this will ensure we always have default working hours
    if (nonWorkingTimes.length === 0) {
        const { workHoursStartTimeInMinutes, workHoursEndTimeInMinutes, workDays } = userOptions;
        nonWorkingTimes = generateNonWorkingHoursBlock(
            workHoursStartTimeInMinutes,
            workHoursEndTimeInMinutes,
            date,
            isPast,
            workDays,
            dayStartMinute,
            dayLength,
            true
        );
    }

    const nonWorkingTimesStyleMode = getIsDarkTheme()
        ? styles.nonWorkingTimeDark
        : styles.nonWorkingTimeLight;
    const elements = nonWorkingTimes.map(({ start, end }) => (
        <div
            key={`${start}_${end}`}
            style={{ top: start, height: end - start }}
            className={classNames(nonWorkingTimesStyleMode, styles.nonWorkingTime)}
        />
    ));

    return <>{elements}</>;
});
